<template>
  <div class="tw-rounded-sm tw-bg-white tw-p-4">
    <div class="tw-relative tw-h-[30rem] tw-w-full">
      <div
        v-if="isFetching"
        class="tw-h-full tw-w-full tw-animate-pulse tw-rounded-sm tw-bg-primary-grey-50/40"
      ></div>
      <div class="tw-relative tw-h-[30rem] tw-w-full" v-else>
        <empty-chart-placeholder v-if="isDataEmpty || isError">
          <template #image>
            <cl-icon-empty-ufo v-if="isError" :width="'216'" :height="'162'" />
            <cl-icon-empty-graph v-else :width="'216'" :height="'162'" />
          </template>
          <template #title>
            <p>{{ placeholderTitle }}</p>
          </template>
          <template #description>
            <p>{{ placeholderMessage }}</p>
          </template>
        </empty-chart-placeholder>
        <template v-else>
          <div class="tw-h-[27rem]">
            <Line
              :data="chartDataset"
              :options="chartOptions"
              height="100%"
              width="100%"
            />
          </div>
          <div class="tw-flex tw-justify-center">
            <div
              v-if="isSatActive || isBothSolutionsDisabled"
              class="tw-flex tw-items-center tw-mx-2"
              @click="toggleIsSatCompletedHidden()"
            >
              <span
                :class="[
                  'tw-flex tw-items-center tw-bg-primary-grey-50 hover:tw-bg-primary-grey-100 tw-p-2 tw-cursor-pointer tw-rounded-md',
                  { 'tw-line-through': isSatCompletedHidden },
                ]"
                role="button"
              >
                <span
                  class="tw-w-4 tw-h-4 tw-bg-[#0e9cff] tw-inline-block tw-rounded-full tw-mr-2"
                ></span>
                {{ t("sat-modules-completed") }}
              </span>
            </div>
            <div
              v-if="isPhsActive || isBothSolutionsDisabled"
              class="tw-flex tw-items-center tw-mx-2"
              @click="toggleIsClickedHidden()"
            >
              <span
                :class="[
                  'tw-flex tw-items-center tw-bg-primary-grey-50 hover:tw-bg-primary-grey-100 tw-p-2 tw-cursor-pointer tw-rounded-md',
                  { 'tw-line-through': isClickedHidden },
                ]"
                role="button"
              >
                <span
                  class="tw-w-4 tw-h-4 tw-bg-[#d23f3f] tw-inline-block tw-rounded-full tw-mr-2"
                ></span>
                {{ t("lures-clicked-legend") }}
              </span>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
interface IProps {
  isFetching: boolean;
  isError: boolean;
  satCompleted: number[];
  luresClicked: number[];
  labels: string[];
  isPhsActive: boolean;
  isSatActive: boolean;
}
</script>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { Line } from "vue-chartjs";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Filler,
} from "chart.js";
import { red, gradientRed, blue, gradientBlue } from "./colors";
import EmptyChartPlaceholder from "@/components/EmptyChartPlaceholder/EmptyChartPlaceholder.vue";
import { useToggle } from "@vueuse/core";

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Filler,
);

const props = defineProps<IProps>();
const { t } = useI18n();
const style = getComputedStyle(document.body);
const textColor = style.getPropertyValue("--chartWhite");

const [isSatCompletedHidden, toggleIsSatCompletedHidden] = useToggle(false);
const [isClickedHidden, toggleIsClickedHidden] = useToggle(false);

const isDataEmpty = computed<boolean>(() => {
  const sentEmpty = props.satCompleted.length === 0;
  const clickedEmpty = props.luresClicked.length === 0;
  const allSentZero =
    props.satCompleted.every((val) => val === 0) &&
    props.luresClicked.every((val) => val === 0);

  return (sentEmpty && clickedEmpty) || allSentZero;
});

const isBothSolutionsDisabled = computed<boolean>(() => {
  return !props.isPhsActive && !props.isSatActive;
});

const maxValue = computed<number>(() => {
  return Math.max(...props.luresClicked, ...props.satCompleted);
});

const placeholderTitle = computed<string>(() => {
  return props.isError ? t("sys-error-default") : t("no-data");
});

const placeholderMessage = computed<string>(() => {
  return props.isError
    ? t("subtext-sys-error-default")
    : t("subtext-modules-vs-lures");
});

const chartDataset = computed(() => {
  const datasets = [];

  if (props.isSatActive || isBothSolutionsDisabled) {
    datasets.push({
      label: t("sat-modules-completed"),
      data: props.satCompleted,
      backgroundColor: gradientBlue,
      pointBackgroundColor: blue.default,
      borderColor: blue.default,
      borderWidth: 2,
      pointRadius: 3,
      tension: 0.4,
      fill: true,
      type: "line" as const,
      yAxisID: "y",
      hidden: isSatCompletedHidden.value,
    });
  }

  if (props.isPhsActive || isBothSolutionsDisabled) {
    datasets.push({
      label: t("lures-clicked-legend"),
      data: props.luresClicked,
      backgroundColor: gradientRed,
      pointBackgroundColor: red.default,
      borderColor: red.default,
      borderWidth: 2,
      pointRadius: 3,
      tension: 0.4,
      fill: true,
      type: "line" as const,
      yAxisID: props.isSatActive || isBothSolutionsDisabled ? "y1" : "y",
      hidden: isClickedHidden.value,
    });
  }

  return {
    labels: props.labels,
    datasets,
  };
});

const chartOptions = computed(() => {
  const scales: Record<string, object> = {
    x: {
      grid: {
        display: false,
        drawTicks: false,
      },
      border: {
        display: false,
      },
      ticks: {
        padding: 10,
        color: textColor,
        font: {
          size: 17,
        },
      },
      offset: false,
    },
  };

  if (props.isSatActive || isBothSolutionsDisabled) {
    scales.y = {
      position: "left" as const,
      beginAtZero: true,
      suggestedMin: 0,
      suggestedMax: maxValue.value,
      title: {
        display: true,
        text: t("sat-modules-completed"),
        color: blue.default,
        font: {
          size: 20,
        },
      },
      border: {
        display: false,
      },
      grid: {
        drawTicks: false,
      },
      ticks: {
        padding: 10,
        precision: 0,
        color: blue.default,
        font: {
          size: 16,
        },
      },
    };
  }

  if (props.isPhsActive || isBothSolutionsDisabled) {
    const yAxisKey = props.isSatActive || isBothSolutionsDisabled ? "y1" : "y";
    scales[yAxisKey] = {
      position:
        props.isSatActive || isBothSolutionsDisabled
          ? "right"
          : ("left" as const),
      beginAtZero: true,
      suggestedMin: 0,
      suggestedMax: maxValue.value,
      title: {
        display: true,
        text: t("lures-clicked-legend"),
        color: red.default,
        font: {
          size: 20,
        },
      },
      border: {
        display: false,
      },
      grid: {
        drawOnChartArea:
          props.isSatActive || isBothSolutionsDisabled ? false : true,
        drawTicks: false,
      },
      ticks: {
        display: true,
        padding: 10,
        precision: 0,
        color: red.default,
        font: {
          size: 16,
        },
      },
    };
  }

  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index" as const,
      intersect: false,
    },
    scales,
    plugins: {
      legend: {
        display: false,
      },
    },
  };
});
</script>
